'use client';

import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { isTauri } from '@/lib/platformUtils';
import {
  clearPersistedAuthPageBrandingCache,
  AUTH_PAGE_BRANDING_STORAGE_KEY,
  type AuthLayout,
  type AuthPageBrandingCachePayload,
  type AuthPageBrandingSnapshot,
  isAuthPageBrandingCacheFresh,
  normalizeAuthLayout,
  persistAuthPageBrandingCache,
  resolveAuthBackgroundUrl,
  sanitizeHostname,
} from '@/lib/authPageBrandingShared';

interface PublicBranding {
  auth_background?: string | null;
  auth_layout?: number | string | null;
  logo_url?: string | null;
}

interface WhitelabelResolution {
  success?: boolean;
  isWhitelabel?: boolean;
  data?: {
    client_name?: string;
    logo_url?: string | null;
    return_url?: string | null;
    auth_background?: string | null;
    auth_layout?: number | string | null;
    allow_signup?: boolean;
    enforce_business_email?: boolean;
    allowed_email_domains?: string[];
  };
}

export interface UseAuthPageBrandingResult {
  isDesktopShell: boolean;
  isNativePlatform: boolean;
  isTauriDesktop: boolean;
  authBackgroundUrl: string | null;
  authLayout: AuthLayout;
  websiteHref: string | null;
  websiteLogoUrl: string | null;
  websiteLogoAlt: string;
  isWhitelabel: boolean;
  allowSignup: boolean;
  enforceBusinessEmail: boolean;
  allowedEmailDomains: string[];
}

interface AuthPageBrandingProviderProps {
  initialBranding: AuthPageBrandingSnapshot;
  initialDesktopShell: boolean;
  children: ReactNode;
}

const DEFAULT_RESULT: UseAuthPageBrandingResult = {
  isDesktopShell: false,
  isNativePlatform: false,
  isTauriDesktop: false,
  authBackgroundUrl: null,
  authLayout: 'right',
  websiteHref: '/',
  websiteLogoUrl: null,
  websiteLogoAlt: 'Website',
  isWhitelabel: false,
  allowSignup: true,
  enforceBusinessEmail: false,
  allowedEmailDomains: [],
};

const AuthPageBrandingContext = createContext<UseAuthPageBrandingResult | null>(null);

function readCachedBranding(hostname: string): AuthPageBrandingSnapshot | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const normalizedHost = sanitizeHostname(hostname);
  const globalPayload = window.__TA_AUTH_PAGE_BRANDING__;
  if (isAuthPageBrandingCacheFresh(globalPayload) && sanitizeHostname(globalPayload.host) === normalizedHost) {
    return globalPayload;
  }

  try {
    const cached = localStorage.getItem(AUTH_PAGE_BRANDING_STORAGE_KEY);
    if (!cached) {
      return null;
    }

    const payload = JSON.parse(cached) as Partial<AuthPageBrandingCachePayload>;
    if (!isAuthPageBrandingCacheFresh(payload) || sanitizeHostname(payload.host) !== normalizedHost) {
      clearPersistedAuthPageBrandingCache();
      return null;
    }

    return {
      host: normalizedHost,
      isWhitelabel: payload.isWhitelabel === true,
      authBackgroundUrl: typeof payload.authBackgroundUrl === 'string' ? payload.authBackgroundUrl : null,
      authLayout: normalizeAuthLayout(payload.authLayout),
      websiteHref: typeof payload.websiteHref === 'string' ? payload.websiteHref : null,
      websiteLogoUrl: typeof payload.websiteLogoUrl === 'string' ? payload.websiteLogoUrl : null,
      websiteLogoAlt: typeof payload.websiteLogoAlt === 'string' ? payload.websiteLogoAlt : 'Website',
      allowSignup: payload.allowSignup !== false,
      enforceBusinessEmail: payload.enforceBusinessEmail === true,
      allowedEmailDomains: Array.isArray(payload.allowedEmailDomains) ? payload.allowedEmailDomains.filter((value): value is string => typeof value === 'string') : [],
    };
  } catch {
    return null;
  }
}

function useStandaloneAuthPageBranding(): UseAuthPageBrandingResult {
  const [isDesktopShell, setIsDesktopShell] = useState(false);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [isTauriDesktop, setIsTauriDesktop] = useState(false);
  const [authBackgroundUrl, setAuthBackgroundUrl] = useState<string | null>(null);
  const [authLayout, setAuthLayout] = useState<AuthLayout>('right');
  const [websiteHref, setWebsiteHref] = useState<string | null>(null);
  const [websiteLogoUrl, setWebsiteLogoUrl] = useState<string | null>(null);
  const [websiteLogoAlt, setWebsiteLogoAlt] = useState('Website');
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const [allowSignup, setAllowSignup] = useState(true);
  const [enforceBusinessEmail, setEnforceBusinessEmail] = useState(false);
  const [allowedEmailDomains, setAllowedEmailDomains] = useState<string[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const syncShellMode = () => {
      const isNative = Capacitor.isNativePlatform();
      const tauriDesktop = isTauri();
      setIsNativePlatform(isNative);
      setIsTauriDesktop(tauriDesktop);
      setIsDesktopShell(!isNative && mediaQuery.matches);
    };

    const frameId = window.requestAnimationFrame(syncShellMode);

    const loadBranding = async () => {
      try {
        const currentOrigin = window.location.origin;
        const [brandingResponse, whitelabelResponse] = await Promise.all([
          fetch(`${currentOrigin}/api/v1/public-branding`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }),
          fetch(`${currentOrigin}/api/v1/whitelabel/resolve`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }),
        ]);

        let publicBranding: PublicBranding | null = null;
        if (brandingResponse.ok) {
          try {
            const brandingResult = await brandingResponse.json();
            if (brandingResult.success && brandingResult.data) {
              publicBranding = brandingResult.data as PublicBranding;
              setAuthBackgroundUrl(resolveAuthBackgroundUrl(brandingResult.data.auth_background));
              setAuthLayout(normalizeAuthLayout(brandingResult.data.auth_layout));
            }
          } catch (parseError) {
            console.warn('[AuthPageBranding] Failed to parse branding response:', parseError);
          }
        }

        if (whitelabelResponse.ok) {
          try {
            const whitelabelResult = (await whitelabelResponse.json()) as WhitelabelResolution;
            if (whitelabelResult.success && whitelabelResult.isWhitelabel && whitelabelResult.data) {
              setIsWhitelabel(true);
              setAllowSignup(whitelabelResult.data.allow_signup === true);
              setEnforceBusinessEmail(whitelabelResult.data.enforce_business_email === true);
              setAllowedEmailDomains(Array.isArray(whitelabelResult.data.allowed_email_domains) ? whitelabelResult.data.allowed_email_domains : []);
              setAuthBackgroundUrl(resolveAuthBackgroundUrl(whitelabelResult.data.auth_background, publicBranding?.auth_background));
              setAuthLayout(normalizeAuthLayout(whitelabelResult.data.auth_layout ?? publicBranding?.auth_layout));
              setWebsiteHref(whitelabelResult.data.return_url || null);
              setWebsiteLogoUrl(whitelabelResult.data.logo_url || publicBranding?.logo_url || null);
              setWebsiteLogoAlt(whitelabelResult.data.client_name || 'Website');
              return;
            }
          } catch (parseError) {
            console.warn('[AuthPageBranding] Failed to parse whitelabel response:', parseError);
          }
        }

        setIsWhitelabel(false);
        setAllowSignup(true);
        setEnforceBusinessEmail(false);
        setAllowedEmailDomains([]);
        setAuthBackgroundUrl(resolveAuthBackgroundUrl(publicBranding?.auth_background));
        setAuthLayout(normalizeAuthLayout(publicBranding?.auth_layout));
        setWebsiteHref('/');
        setWebsiteLogoUrl(publicBranding?.logo_url || null);
        setWebsiteLogoAlt('Total Access Solutions');
      } catch {
        setWebsiteHref('/');
      }
    };

    void loadBranding();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncShellMode);
      return () => {
        window.cancelAnimationFrame(frameId);
        mediaQuery.removeEventListener('change', syncShellMode);
      };
    }

    mediaQuery.addListener(syncShellMode);
    return () => {
      window.cancelAnimationFrame(frameId);
      mediaQuery.removeListener(syncShellMode);
    };
  }, []);

  return {
    isDesktopShell,
    isNativePlatform,
    isTauriDesktop,
    authBackgroundUrl,
    authLayout,
    websiteHref,
    websiteLogoUrl,
    websiteLogoAlt,
    isWhitelabel,
    allowSignup,
    enforceBusinessEmail,
    allowedEmailDomains,
  };
}

export function AuthPageBrandingProvider({
  initialBranding,
  initialDesktopShell,
  children,
}: AuthPageBrandingProviderProps) {
  const [branding, setBranding] = useState<AuthPageBrandingSnapshot>(() => ({
    ...initialBranding,
    host: sanitizeHostname(initialBranding.host),
  }));
  const [isDesktopShell, setIsDesktopShell] = useState(initialDesktopShell);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [isTauriDesktop, setIsTauriDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const syncShellMode = () => {
      const isNative = Capacitor.isNativePlatform();
      const tauriDesktop = isTauri();
      setIsNativePlatform(isNative);
      setIsTauriDesktop(tauriDesktop);
      setIsDesktopShell(!isNative && mediaQuery.matches);
    };

    const frameId = window.requestAnimationFrame(syncShellMode);

    const loadBranding = async () => {
      try {
        const currentOrigin = window.location.origin;
        const [brandingResponse, whitelabelResponse] = await Promise.all([
          fetch(`${currentOrigin}/api/v1/public-branding`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }),
          fetch(`${currentOrigin}/api/v1/whitelabel/resolve`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }),
        ]);

        let publicBranding: PublicBranding | null = null;
        if (brandingResponse.ok) {
          const brandingResult = await brandingResponse.json();
          if (brandingResult.success && brandingResult.data) {
            publicBranding = brandingResult.data as PublicBranding;
          }
        }

        if (whitelabelResponse.ok) {
          const whitelabelResult = (await whitelabelResponse.json()) as WhitelabelResolution;
          if (whitelabelResult.success && whitelabelResult.isWhitelabel && whitelabelResult.data) {
            setBranding({
              host: sanitizeHostname(window.location.hostname),
              isWhitelabel: true,
              authBackgroundUrl: resolveAuthBackgroundUrl(whitelabelResult.data.auth_background, publicBranding?.auth_background),
              authLayout: normalizeAuthLayout(whitelabelResult.data.auth_layout ?? publicBranding?.auth_layout),
              websiteHref: whitelabelResult.data.return_url || null,
              websiteLogoUrl: whitelabelResult.data.logo_url || publicBranding?.logo_url || null,
              websiteLogoAlt: whitelabelResult.data.client_name || 'Website',
              allowSignup: whitelabelResult.data.allow_signup === true,
              enforceBusinessEmail: whitelabelResult.data.enforce_business_email === true,
              allowedEmailDomains: Array.isArray(whitelabelResult.data.allowed_email_domains) ? whitelabelResult.data.allowed_email_domains : [],
            });
            return;
          }
        }

        setBranding({
          host: sanitizeHostname(window.location.hostname),
          isWhitelabel: false,
          authBackgroundUrl: resolveAuthBackgroundUrl(publicBranding?.auth_background),
          authLayout: normalizeAuthLayout(publicBranding?.auth_layout),
          websiteHref: '/',
          websiteLogoUrl: publicBranding?.logo_url || null,
          websiteLogoAlt: 'Total Access Solutions',
          allowSignup: true,
          enforceBusinessEmail: false,
          allowedEmailDomains: [],
        });
      } catch {
        setBranding((currentBranding) => ({
          ...currentBranding,
          websiteHref: currentBranding.websiteHref || '/',
        }));
      }
    };

    void loadBranding();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncShellMode);
      return () => {
        window.cancelAnimationFrame(frameId);
        mediaQuery.removeEventListener('change', syncShellMode);
      };
    }

    mediaQuery.addListener(syncShellMode);
    return () => {
      window.cancelAnimationFrame(frameId);
      mediaQuery.removeListener(syncShellMode);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    persistAuthPageBrandingCache({
      ...branding,
      host: sanitizeHostname(window.location.hostname),
    });
  }, [branding]);

  const value = useMemo<UseAuthPageBrandingResult>(() => ({
    isDesktopShell,
    isNativePlatform,
    isTauriDesktop,
    authBackgroundUrl: branding.authBackgroundUrl,
    authLayout: branding.authLayout,
    websiteHref: branding.websiteHref,
    websiteLogoUrl: branding.websiteLogoUrl,
    websiteLogoAlt: branding.websiteLogoAlt,
    isWhitelabel: branding.isWhitelabel,
    allowSignup: branding.allowSignup,
    enforceBusinessEmail: branding.enforceBusinessEmail,
    allowedEmailDomains: branding.allowedEmailDomains,
  }), [branding, isDesktopShell, isNativePlatform, isTauriDesktop]);

  return createElement(AuthPageBrandingContext.Provider, { value }, children);
}

export function useResolvedAuthPageBranding(): UseAuthPageBrandingResult {
  return useContext(AuthPageBrandingContext) || DEFAULT_RESULT;
}

export default function useAuthPageBranding(): UseAuthPageBrandingResult {
  return useStandaloneAuthPageBranding();
}